import { isEmpty } from 'lodash';
import moment from 'moment';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';

export const toFilterEntries = (value) =>
  Array.isArray(value) ? value.filter((entry) => !!entry && typeof entry.slug === 'string') : [];

export const isEmptyFilters = (filters) => {
  if (isEmpty(filters)) {
    return true;
  }

  return Object.values(filters).every((filter) => filter.length === 0);
};

export const counterFormatter = (value) => {
  if (Array.isArray(value)) {
    return value.length;
  }

  if (Number.isInteger(value)) {
    return value;
  }

  if (value && moment(value, API_DATE_FORMAT).isValid) {
    return 1;
  }

  return null;
};

const isEmptyValue = (value) => isEmpty(value) && !Number.isInteger(value);

// accept filters e.g.  [ { id: 1, slug: 'name' } ]
// returns [ { id: 1, slug: 'name', type: 'basic', value: [1,2,3] } ]
export const formatOptions = ({ options, type, formatSlug, filters }) =>
  (options?.[type] || []).map((option) => ({
    // Keep caller-supplied props (extraFilters' name/options)
    ...(typeof option === 'object' ? option : {}),
    id: option?.id ?? option,
    slug: formatSlug?.(option) ?? option?.slug ?? option,
    type,
    value: filters[type]?.find((filter) => filter.id === (option?.id ?? option))?.value
  }));

export const mergeExtraFilters = (registered, extraFilters) =>
  extraFilters
    ? {
        ...registered,
        basic: [...(registered?.basic || []), ...(extraFilters.basic || [])],
        fields: [...(registered?.fields || []), ...(extraFilters.fields || [])]
      }
    : registered;

// accept key (basic), id (1), value ('new value'), allFilters ({})
// returns { basic: [id: 1, value: 'new value'] }
export const applyFilter = ({ type, slug, id, value, allFilters }) => {
  if (isEmptyValue(value)) {
    return { ...allFilters, [type]: (allFilters[type] || []).filter((filter) => filter.id !== id) };
  }

  const isValueExists = (allFilters[type] || []).find((filter) => filter.id === id);

  return {
    ...allFilters,
    [type]: isValueExists
      ? allFilters[type].map((filter) => (filter.id === id ? { ...filter, value } : filter))
      : [...(allFilters[type] || []), { id, slug, value }]
  };
};

// accept map values e.g. { basic: { 1: {value, slug} }
// returns { basic: [id: 1, value: 'value', slug] }
export const applyFilters = ({ values, allFilters }) =>
  Object.entries(values).reduce((all, [type, valuesData]) => {
    const existing = allFilters[type] || [];
    const updatedEntries = Object.entries(valuesData);

    const parseFilterId = (id) => (/^\d+$/.test(id) ? parseInt(id, 10) : id);

    const updated = updatedEntries
      .filter(([, filterData]) => !isEmptyValue(filterData.value))
      .map(([id, filterData]) => {
        const parsedId = parseFilterId(id);
        return {
          id: parsedId,
          value: filterData.value,
          slug: filterData.slug
        };
      });

    const updatedIds = new Set(updated.map((currFilter) => currFilter.id));

    const removedIds = new Set(
      updatedEntries.filter(([, filterData]) => isEmptyValue(filterData.value)).map(([id]) => parseFilterId(id))
    );

    // Keep existing filters that are not updated or removed
    const preserved = existing.filter((currFilter) => !updatedIds.has(currFilter.id) && !removedIds.has(currFilter.id));

    return {
      ...all,
      [type]: [...preserved, ...updated]
    };
  }, {});

// accept key (basic), id (1), value ('new value'), allFilters ({})
// returns { basic: [id: 1, value: 'new value'] }
export const removeFilter = ({ type, id, allFilters }) => {
  return { ...allFilters, [type]: allFilters?.[type]?.filter((filter) => filter.id !== id) };
};
